import React from 'react';
import { Box, Chip } from '@mui/material';

/**
 * PulseHighlight component wraps any UI element and applies a pulse glow animation + badge hint when active.
 * 
 * @param {Object} props
 * @param {boolean} props.active - Whether the highlight effect is active
 * @param {string} [props.message] - Optional message for the hint badge
 * @param {string} [props.color='primary'] - Color palette for the badge and glow ('primary', 'secondary', etc.)
 * @param {React.ReactNode} props.children - Target element to highlight
 * @param {Object} [props.sx] - Additional container sx styling
 */
export const PulseHighlight = ({
    active = false,
    message = '',
    color = 'primary',
    children,
    sx = {}
}) => {
    return (
        <Box sx={{ position: 'relative', width: '100%', ...sx }}>
            {active && message && (
                <Chip
                    label={message}
                    color={color}
                    size="small"
                    sx={{
                        mb: 1.5,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        width: '100%',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                        animation: 'pulseBadge 1.5s infinite ease-in-out',
                        '@keyframes pulseBadge': {
                            '0%, 100%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.03)' },
                        }
                    }}
                />
            )}
            <Box
                sx={{
                    transition: 'all 0.3s ease',
                    borderRadius: 1,
                    ...(active && {
                        animation: 'pulseGlow 1.5s infinite ease-in-out',
                        '@keyframes pulseGlow': {
                            '0%': {
                                boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.6)',
                            },
                            '50%': {
                                boxShadow: '0 0 0 8px rgba(25, 118, 210, 0)',
                            },
                            '100%': {
                                boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${color}.main`,
                            borderWidth: 2,
                            opacity: 1
                        }
                    })
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default PulseHighlight;
